<template>
  <v-app id="aqualys_crm">
    <!-- Progress bar - using NProgress via router hooks in main.ts -->

    <template v-if="!signedIn">
      <router-view></router-view>
    </template>

    <template v-if="signedIn">
      <v-navigation-drawer
        v-model="drawer"
        :rail="rail"
        rail-width="70"
        width="250"
        class="bg-blue-lighten-5"
      >
        <!-- USER HEADER -->
        <v-list density="compact" class="pa-0">
          <v-list-item class="drawer-header">
            <template #prepend>
              <v-avatar size="36" class="cursor-pointer" :image="userAvatar" @click="rail = false" />
            </template>

            <v-list-item-title v-if="!rail">
              {{ user.firstname }} {{ user.lastname }}
            </v-list-item-title>
            <v-list-item-subtitle v-if="!rail">
              {{ user.username }}
            </v-list-item-subtitle>

            <template #append v-if="!rail">
              <v-menu location="bottom end">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-dots-vertical"
                    size="small"
                    variant="text"
                  />
                </template>

                <v-list density="compact">
                  <v-list-item
                    v-for="item in userMenus"
                    :key="item.title"
                    :title="item.title"
                    @click="handleUserActions(item)"
                  />
                </v-list>
              </v-menu>

              <v-btn icon size="small" variant="text" @click.stop="rail = true">
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
            </template>
          </v-list-item>
        </v-list>

        <!-- NAVIGATION -->
        <v-list nav density="compact">
          <v-list-item
            v-for="item in visibleItems"
            :key="item.title"
            :title="item.title"
            :prepend-icon="item.icon"
            :to="{ name: item.link }"
            exact
          />
        </v-list>
      </v-navigation-drawer>

      <v-app-bar app elevation="1">
        <v-app-bar-nav-icon @click.stop="drawer = !drawer" light></v-app-bar-nav-icon>
        <v-spacer></v-spacer>
        <div class="text-xs-center pr-3 mt-2">
          <v-badge left class="mr-3">
            <template v-slot:badge>6</template>
            <v-icon large color="grey lighten-1">mdi-bell-outline</v-icon>
          </v-badge>

          <v-badge color="red">
            <template v-slot:badge>!</template>
            <v-icon large color="grey">mdi-email-outline</v-icon>
          </v-badge>
        </div>

        <v-btn light text href="https://www.aqualyslaveries.com/" target="_blank">
          <v-icon large color="black lighten-1">mdi-washing-machine</v-icon>
          <span style="margin-left: 0.4rem">AQUALYS</span>
        </v-btn>
      </v-app-bar>

      <v-main>
        <v-container fluid>
          <router-view></router-view>
        </v-container>
      </v-main>

      <v-footer :inset="true" style="justify-content: center; text-align: center" app>
        <span>&copy; AQUALYS {{ yearRange }} ({{ buildVersion }})</span>
      </v-footer>
    </template>
    <AppNotification />
  </v-app>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useTheme } from "vuetify";
import { useSessionStore } from "@/store/session";
import { useAppStore } from "@/store";
import { useRouter } from "vue-router";
import AppNotification from "@/components/AppNotification.vue";
import defaultAvatar from "@/assets/avatar0.png";
import avatar1 from "@/assets/img/avatar1.png";
import avatar2 from "@/assets/img/avatar2.png";
import avatar3 from "@/assets/img/avatar3.png";
import avatar4 from "@/assets/img/avatar4.png";
import avatar5 from "@/assets/img/avatar5.png";

const avatarMap: Record<string, string> = {
  "avatar1.png": avatar1,
  "avatar2.png": avatar2,
  "avatar3.png": avatar3,
  "avatar4.png": avatar4,
  "avatar5.png": avatar5,
};

const START_YEAR = 2021;
const currentYear = new Date().getFullYear();
const yearRange = computed(() =>
  currentYear > START_YEAR ? `${START_YEAR}-${currentYear}` : `${START_YEAR}`
);
const buildVersion = __APP_VERSION__;

const router = useRouter();
const theme = useTheme();
const appStore = useAppStore();
const sessionStore = useSessionStore();
const { isSignedIn: signedIn, loggedUser: user } = storeToRefs(sessionStore);

const userAvatar = computed(() => {
  const name = user.value?.avatar;
  return (name && avatarMap[name]) ? avatarMap[name] : defaultAvatar;
});

const drawer = ref(window.innerWidth > 960);
const rail = ref(false);

interface AppMenu {
  icon: string;
  title: string;
  vertical?: string;
  action?: () => void;
  link?: string;
  roles?: string[];
}

const visibleItems = computed(() => {
  return navigationItems.filter(
    (item) =>
      !item.roles || item.roles.some((role) => sessionStore.userRoles.includes(role))
  );
});

const navigationItems: AppMenu[] = [
  {
    icon: "mdi-view-dashboard",
    title: "Tableau de bord",
    vertical: "Tableau de bord",
    link: "dashboard",
  },
  {
    icon: "mdi-shield-account-outline",
    title: "Sièges",
    vertical: "Sièges",
    link: "accounts",
    roles: ["ROLE_ADMIN", "ROLE_ACCOUNT"],
  },
  {
    icon: "mdi-washing-machine",
    title: "Laveries",
    vertical: "Laveries",
    link: "laveries",
    roles: ["ROLE_ADMIN", "ROLE_LAVERIE"],
  },
  {
    icon: "mdi-book-multiple",
    title: "Produits",
    vertical: "Produits",
    link: "products",
    roles: ["ROLE_ADMIN", "ROLE_PRODUCT"],
  },
  {
    icon: "mdi-scissors-cutting",
    title: "Pièces détachées",
    vertical: "Pièces détachées",
    link: "spareparts",
    roles: ["ROLE_ADMIN", "ROLE_SPARPART"],
  },
  {
    icon: "mdi-file-word-outline",
    title: "Editions",
    vertical: "Editions",
    link: "editions",
    roles: ["ROLE_ADMIN", "ROLE_EDITION"],
  },
  {
    icon: "mdi-hammer-wrench",
    title: "Tournées",
    vertical: "Tournées",
    link: "tasklists",
    roles: ["ROLE_ADMIN", "ROLE_TOURNEE"],
  },
  {
    icon: "mdi-format-list-checks",
    title: "Activités",
    vertical: "Activités",
    link: "tasks",
    roles: ["ROLE_ADMIN", "ROLE_TOURNEE"],
  },
  {
    icon: "mdi-face-agent",
    title: "Tickets Support",
    vertical: "Tickets Support",
    link: "supporttickets",
    roles: ["ROLE_ADMIN"],
  },
  {
    icon: "mdi-car",
    title: "Véhicules",
    vertical: "Véhicules",
    link: "vehicles",
    roles: ["ROLE_ADMIN", "ROLE_VEHICULE"],
  },
  {
    icon: "mdi-account-group",
    title: "Utilisateurs",
    vertical: "Utilisateurs",
    link: "users",
    roles: ["ROLE_ADMIN", "ROLE_USER"],
  },
  {
    icon: "mdi-information-outline",
    title: "About",
    vertical: "About",
    link: "about",
  },
];

const userMenus = ref<AppMenu[]>([
  {
    icon: "mdi-theme-light-dark",
    title: "Changer de thème",
    action: () => appStore.toggleTheme(),
  },
  {
    icon: "bubble_chart",
    title: "Changer le mot de passe",
    link: "ChangePassword",
  },
  {
    icon: "bubble_chart",
    title: "Meta Données",
    link: "metadata",
  },
  {
    icon: "bubble_chart",
    title: "Déconnexion",
    action: () => sessionStore.logout(),
  },
]);

const handleUserActions = async (item: AppMenu) => {
  if (item.action) {
    item.action();
    return;
  }
  if (item.link) {
    router.push({ name: item.link });
  }
};

watch(
  () => appStore.theme,
  (newTheme) => {
    theme.change(newTheme);
  },
  { immediate: true }
);
</script>

<style scoped>
.drawer-header {
  cursor: pointer;
}
</style>
